import {Badge} from "primereact/badge";
import {Message} from "primereact/message";
import {TabPanel, TabView} from "primereact/tabview";
import {Tooltip} from "primereact/tooltip";
import type {ReactNode} from "react";
import {useEffect, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import {matchPath, Outlet, useLocation} from "react-router-dom";

import type {RevaFilterTab} from "../api/payload-types/search-valuation";
import I18nErrorMessage from "../component/I18nErrorMessage";
import LoadingIndicator from "../component/LoadingIndicator";
import {MainFrame} from "../component/MainFrame";
import {useNavigateWithQuery, useNavigationPending} from "../hooks/navigationHooks";
import {
    useCheckPermission,
    useCheckPermissionRequirement,
    useCreatablePurposes,
    useIsResearchOnly,
    useIsRevisionReadOnly,
} from "../hooks/permissionHooks";
import {selectLoading as selectAddressLoading, selectError as selectAddressQueryError} from "../redux/addressSlice";
import {selectPermissions} from "../redux/authSlice";
import {
    selectPersistenceInitialized,
    selectPersistenceLoading,
    selectPersistenceQueryError,
    selectPurpose,
    selectRevaId,
} from "../redux/persistenceSlice";
import {selectFreeText, selectTabCounts} from "../redux/searchValuationsSlice";
import {
    PERMISSION_REQUIREMENT_VALUE_NON_SNAPSHOTS,
    PERMISSION_RULE_CORRECTION,
    PERMISSION_RULE_OWNERSHIP_ADMIN,
    PERMISSION_RULE_PURPOSE_RESEARCH,
} from "../util/permissions";
import {valuationEntryRoute} from "../util/purposeResolution";
import AutoDetectMultiInput from "./start/AutoDetectMultiInput";

/** One tab of the start-page navigation bar. Its content lives in the router as a child route. */
interface StartTab {
    /** Sub-path under /start (route + activeIndex key). */
    route: string;
    /** Rendered tab header (its text is the accessible name the e2e tests select by). */
    header: ReactNode;
    /**
     * Weitere Unterpfade, die denselben Tab zeigen: `/start/valuation` (Bewertungs-Einstieg, #19481) ist der
     * Standard-Reiter „Meine Bewertungen", legt aber neue REVA-IDs direkt als Bewertung an.
     */
    aliases?: string[];
}

/**
 * Start page (#19113): the address/ID search on top, below it a tab bar that navigates between the
 * start-page sub-routes — WIP (`/start/wip`, the user's own still-editable valuations), POOL
 * (`/start/pool`, unassigned valuations anyone could take over), DONE (`/start/history`, the user's
 * own snapshots) plus the permission-gated CORRECTION (`/start/correction`) and TEAMADMIN (`/start/teamadmin`).
 * Each tab is a real route, so tabs are deep-linkable, survive reload and work with the browser's
 * back/forward buttons; the tab bar is only the switcher (`panelContainerClassName="hidden"`), the
 * active route's content renders in the {@link Outlet}. The permission gating and the redirect of
 * `/start` to the first allowed sub-route live in the router (see router.tsx); the per-tab search
 * state lives in searchValuationsSlice and is therefore independent of this component's mounting.
 *
 * #19125: a read-only revision user ({@code reva-lifecycle-revision-readonly}) sees NONE of the five tabs above
 * but a single, exclusive tab "Alle Bewertungen" (`/start/revision`) listing every snapshot valuation of every
 * user. The router enforces the same exclusivity (index redirects to /start/revision, the normal tabs bounce back).
 *
 * #19481: The RESEARCH tab (`/start/research`, "Research-Abfragen", only with reva-purpose-research) lists the
 * user's own research requests; research requests appear nowhere else. It is also the research entry URL: a new REVA-ID
 * created there is a research request without asking. `/start/valuation` is the matching valuation entry URL (shows
 * the WIP tab). A research-only user (reva-purpose-research without reva-purpose-valuation/admins) sees only the
 * research tab. Without any purpose permission the user may not create REVA-IDs at all, which a hint below the search
 * explains.
 */
function PageStart() {
    const {t} = useTranslation();
    const navigate = useNavigateWithQuery();
    const navigationPending = useNavigationPending();
    const revaId = useSelector(selectRevaId);
    const purpose = useSelector(selectPurpose);
    const permissions = useSelector(selectPermissions);
    const persistenceLoading = useSelector(selectPersistenceLoading);
    const persistenceInitialized = useSelector(selectPersistenceInitialized);
    const location = useLocation();

    const persistenceQueryError = useSelector(selectPersistenceQueryError);
    const addressQueryError = useSelector(selectAddressQueryError);
    const addressLoading = useSelector(selectAddressLoading);
    // Snapshots always carry the value-indication result, so a snapshot needs no indication
    // permission to be viewed on /object; only live valuations require it. See #18799. This relies
    // on isSnapshot being set before persistenceLoading turns false (see persistenceThunk).
    const hasAnyPermissions = useCheckPermissionRequirement(PERMISSION_REQUIREMENT_VALUE_NON_SNAPSHOTS);

    const loading = addressLoading || persistenceLoading || !persistenceInitialized;

    const canCorrect = useCheckPermission(PERMISSION_RULE_CORRECTION);
    const canAdmin = useCheckPermission(PERMISSION_RULE_OWNERSHIP_ADMIN);
    // #19125: the read-only revision role sees a single, exclusive tab ("Alle Bewertungen") instead of the
    // five normal start-page tabs. The router mirrors this (index redirects here, normal tabs bounce back).
    const isRevision = useIsRevisionReadOnly();
    const canResearch = useCheckPermission(PERMISSION_RULE_PURPOSE_RESEARCH);
    // #19481: Research-only-User sehen nur „Research-Abfragen" (der Router leitet alle anderen Reiter um).
    const isResearchOnly = useIsResearchOnly();
    const mayCreate = useCreatablePurposes().length > 0;

    // Live-Freitextfilter + die davon abhängigen Tab-Trefferzahlen (Badges). Die Badges erscheinen NUR,
    // solange ein Freitext aktiv ist (>= 3 Zeichen); die Zahlen kommen gebündelt aus dem tab-counts-Endpunkt.
    const freeText = useSelector(selectFreeText);
    const {tabCounts, snapshotCounts} = useSelector(selectTabCounts);
    const filterActive = !!freeText;

    // Trefferzahl je Tab-Route: die Bewertungs-Tabs aus tabCounts (Route = RevaFilterTab in Kleinschreibung),
    // der Historie-Tab (DONE) aus den Snapshot-Counts (eigene). Fehlender Key = keine Badge.
    const badgeCount = (route: string): number | undefined =>
        route === "history" ? snapshotCounts.own : tabCounts[route.toUpperCase() as RevaFilterTab];

    // Tab-Header mit Tooltip — die i18n-Keys/CSS-Klassen folgen alle demselben Schema
    // (start.tabs.<name> / start.tabs.<name>Tooltip / tabHeader-<name>).
    const tabHeaderContent = (name: string) => (
        <span className={`tabHeader-${name}`}>
            {t(`start.tabs.${name}`)}
            <Tooltip target={`.tabHeader-${name}`} position="top">
                {t(`start.tabs.${name}Tooltip`)}
            </Tooltip>
        </span>
    );

    // Header-Inhalt + (nur bei aktivem Freitext und Treffern > 0) eine gelbe Treffer-Badge rechts
    // daneben. Die Wrapper-Klasse tabFilterBadge-<route> ist ein stabiler e2e-Selektor.
    const withBadge = (route: string, content: ReactNode): ReactNode => {
        const count = badgeCount(route);
        const showBadge = filterActive && (count ?? 0) > 0;
        return (
            <span className="inline-flex items-center gap-2">
                {content}
                {showBadge && (
                    <span
                        className={`tabFilterBadge tabFilterBadge-${route}`}
                        role="img"
                        aria-label={t("start.tabs.filterCount", {count})}
                    >
                        <Badge value={count} severity="warning" />
                    </span>
                )}
            </span>
        );
    };

    // The visible tabs, in bar order. CORRECTION/TEAMADMIN only appear with their permission; the router
    // guards direct URL access to those routes the same way (see router.tsx). Route names are stable —
    // Header.tsx deep-links to /start/history, and the router's /start index redirects to the first entry.
    // Memoized: die Header enthalten PrimeReact-Tooltips, die sonst bei jedem Render (z.B. jeder
    // Navigation unter /start/*) neu aufgebaut würden; die Badge-Zahlen (freeText/tabCounts/snapshotCounts)
    // sind daher zusätzlich Dependencies, damit die Badges bei Änderung neu gerendert werden.
    const tabs: StartTab[] = useMemo(
        () =>
            isRevision
                ? [{route: "revision", header: withBadge("revision", t("start.tabs.revision"))}]
                : isResearchOnly
                  ? [{route: "research", header: withBadge("research", tabHeaderContent("research"))}]
                  : [
                        {route: "wip", header: withBadge("wip", t("start.tabs.wip")), aliases: ["valuation"]},
                        {route: "pool", header: withBadge("pool", tabHeaderContent("pool"))},
                        {route: "history", header: withBadge("history", tabHeaderContent("done"))},
                        ...(canCorrect
                            ? [{route: "correction", header: withBadge("correction", tabHeaderContent("correction"))}]
                            : []),
                        ...(canAdmin
                            ? [{route: "teamadmin", header: withBadge("teamadmin", tabHeaderContent("teamadmin"))}]
                            : []),
                        ...(canResearch
                            ? [{route: "research", header: withBadge("research", tabHeaderContent("research"))}]
                            : []),
                    ],
        [t, isRevision, isResearchOnly, canCorrect, canAdmin, canResearch, filterActive, tabCounts, snapshotCounts],
    );

    const activeIndex = Math.max(
        0,
        tabs.findIndex((tab) =>
            [tab.route, ...(tab.aliases ?? [])].some((route) => matchPath(`/start/${route}`, location.pathname)),
        ),
    );

    const onTabChange = (index: number) => {
        if (index >= 0 && index < tabs.length) {
            navigate(`/start/${tabs[index].route}`);
        }
    };

    useEffect(() => {
        // Mirror image of the revaId guard in util/hooks: while a navigation is pending, the
        // pathname below is still the one being left, so this would bounce a target that the app
        // is on its way to - and abort that navigation in the process.
        if (navigationPending()) {
            return;
        }
        if (location.pathname.startsWith("/start") && revaId && !persistenceLoading && persistenceInitialized) {
            // Nach dem Laden/Anlegen (Suchfeld, ?search=, Listen, Doppler-Dialog) auf die Einstiegsseite des Zwecks:
            // Research startet auf der Lage, Bewertungen auf der Objekt-Übersicht (#19481).
            navigate(valuationEntryRoute(purpose, permissions, hasAnyPermissions));
        }
    }, [
        navigate,
        navigationPending,
        location,
        revaId,
        persistenceLoading,
        persistenceInitialized,
        hasAnyPermissions,
        purpose,
        permissions,
    ]);

    return (
        <MainFrame>
            <h2 className="flex mt-0 mb-2">
                <AutoDetectMultiInput className="flex-1" />
            </h2>
            {!mayCreate && !isRevision && (
                <Message
                    id="noCreatePermissionHint"
                    severity="info"
                    className="w-full mb-2 justify-start"
                    text={t("purpose.noCreatePermission")}
                />
            )}
            <LoadingIndicator className="flex-1" loading={loading} />
            <I18nErrorMessage errorObject={persistenceQueryError} />
            <I18nErrorMessage errorObject={addressQueryError} />
            {/* The tab bar is only the switcher; each tab's content is the active child route, rendered
                on demand in the Outlet (so a tab's data is loaded only once its route is active). */}
            {!loading && (
                <>
                    <TabView
                        className="mt-0 HeaderTabView"
                        panelContainerClassName="hidden"
                        activeIndex={activeIndex}
                        onTabChange={(e) => onTabChange(e.index)}
                    >
                        {tabs.map((tab) => (
                            <TabPanel key={tab.route} header={tab.header} />
                        ))}
                    </TabView>
                    <div className="flex-1 flex flex-col">
                        <Outlet />
                    </div>
                </>
            )}
        </MainFrame>
    );
}

export default PageStart;
